/**
 * Phone control for posing the robot.
 *
 * The desktop joint list stacks every joint in the inspector, which on a phone
 * means the panel covers the robot you are trying to look at. This shows one
 * joint at a time in a short sheet, so most of the viewport stays visible while
 * the joint moves.
 */

import { useEffect, useMemo, useRef } from "react";
import { ActionIcon, Box, Button, Group, ScrollArea, Slider, Stack, Text } from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useJoints, useSelectedGroupFilter, useSelectedJointName, useViewerStore } from "../store";
import { useWebSocket } from "../WebSocket";
import { useJointSliderControl } from "./right/useJointSliderControl";
import { formatDisplayValue, getJointUnit, snapDisplayValue } from "./right/jointUnits";

/** Big enough to hit without aiming; 44px is the usual touch-target floor. */
const STEP_BUTTON_SIZE = 44;

/** Short label for a joint: the prefix is the module, which the chips repeat. */
function shortJointName(name: string, displayName: string | null): string {
  if (displayName) {
    return displayName;
  }
  const parts = name.split("/");
  return parts.length > 1 ? parts.slice(1).join("/") : name;
}

interface MobileJointSheetProps {
  /**
   * Trade the roomier layout for height.
   *
   * In AR the sheet sits over the robot in the room, so the name and the value
   * share a row and the range labels go: the slider's ends already say where
   * the limits are.
   */
  compact?: boolean;
}

export function MobileJointSheet({ compact = false }: MobileJointSheetProps = {}) {
  const allJoints = useJoints();
  const selectedGroupFilter = useSelectedGroupFilter();
  const { changeJointValue, dragJointValue, commitJointValue } = useJointSliderControl();
  const { sendHighlightJoint } = useWebSocket();

  const joints = useMemo(
    () =>
      allJoints.filter((joint) =>
        selectedGroupFilter ? joint.groups.includes(selectedGroupFilter) : true
      ),
    [allJoints, selectedGroupFilter]
  );

  // In the store, not in this component: tapping a part of the robot in AR
  // picks its joint, and that has to land here.
  const selectedName = useSelectedJointName();
  const setSelectedName = useViewerStore((state) => state.setSelectedJointName);
  const selectedChipRef = useRef<HTMLButtonElement>(null);

  // The chip row scrolls, so bring the active joint back into view when the
  // selection moves — on open, or after a filter change picks a new default.
  useEffect(() => {
    selectedChipRef.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [selectedName]);

  // Keep the selection pointing at a joint that still exists: modules get
  // deleted and group filters change underneath this.
  useEffect(() => {
    if (joints.length === 0) {
      if (selectedName !== null) {
        setSelectedName(null);
      }
      return;
    }
    if (!selectedName || !joints.some((joint) => joint.name === selectedName)) {
      setSelectedName(joints[0].name);
    }
  }, [joints, selectedName, setSelectedName]);

  // The desktop list highlights a joint in the scene while the cursor rests on
  // its name. There is no hover on touch, so the joint being edited is the one
  // that gets highlighted, and the highlight is dropped when the sheet closes.
  useEffect(() => {
    if (!selectedName) {
      return;
    }
    sendHighlightJoint(selectedName);
    return () => sendHighlightJoint("");
  }, [selectedName, sendHighlightJoint]);

  const index = joints.findIndex((joint) => joint.name === selectedName);
  const joint = index >= 0 ? joints[index] : null;

  if (!joint) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed" ta="center">
          {allJoints.length === 0
            ? "No joints yet — add a module with a servo to pose it here."
            : "No joints in the selected group."}
        </Text>
      </Box>
    );
  }

  const unit = getJointUnit(joint.joint_type);
  const minDisplay = unit.toDisplay(joint.min_angle);
  const maxDisplay = unit.toDisplay(joint.max_angle);
  const sliderMin = Math.min(minDisplay, maxDisplay);
  const sliderMax = Math.max(minDisplay, maxDisplay);
  const displayValue = unit.toDisplay(joint.current_angle);

  const step = (delta: number) => {
    const next = Math.max(sliderMin, Math.min(sliderMax, displayValue + delta * unit.step));
    const native = unit.toNative(next);
    changeJointValue(joint.name, native);
    // A step is a complete gesture, so it commits straight away rather than
    // waiting for a drag to end.
    commitJointValue(joint.name, native);
  };

  const shortName = shortJointName(joint.name, joint.display_name);

  return (
    <Stack gap="xs" p={compact ? "xs" : "md"}>
      {/* Every joint is one tap away. A row of chips rather than a dropdown so
          switching costs no menu, and horizontal so the sheet stays short. */}
      <ScrollArea type="auto" scrollbarSize={6} offsetScrollbars={false}>
        <Group gap={6} wrap="nowrap" pb={4}>
          {joints.map((candidate) => {
            const isSelected = candidate.name === joint.name;
            return (
              <Button
                key={candidate.name}
                ref={isSelected ? selectedChipRef : undefined}
                size="sm"
                variant={isSelected ? "filled" : "default"}
                onClick={() => setSelectedName(candidate.name)}
                style={{ flexShrink: 0 }}
              >
                {shortJointName(candidate.name, candidate.display_name)}
              </Button>
            );
          })}
        </Group>
      </ScrollArea>

      {/* Full name, so two modules whose joints share a short name can still be
          told apart — the chips alone would look identical. */}
      {compact ? (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text size="xs" c="dimmed" lineClamp={1} style={{ wordBreak: "break-all" }}>
            {joint.name}
          </Text>
          <Text size="lg" fw={700} style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
            {formatDisplayValue(displayValue, unit)}
          </Text>
        </Group>
      ) : (
        <>
          <Text size="xs" c="dimmed" ta="center" lineClamp={1} style={{ wordBreak: "break-all" }}>
            {joint.name}
          </Text>

          <Text ta="center" size="xl" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatDisplayValue(displayValue, unit)}
          </Text>
        </>
      )}

      <Group wrap="nowrap" gap="sm">
        <ActionIcon
          variant="default"
          size={STEP_BUTTON_SIZE}
          aria-label={`Decrease ${shortName}`}
          onClick={() => step(-1)}
        >
          <IconMinus size={20} />
        </ActionIcon>
        <Slider
          style={{ flex: 1 }}
          // Larger than the desktop list's "sm": this thumb is dragged with a
          // fingertip rather than a cursor.
          size="xl"
          thumbSize={32}
          value={displayValue}
          min={sliderMin}
          max={sliderMax}
          step={unit.step}
          label={(value) => formatDisplayValue(value, unit)}
          onChange={(value) => {
            const snapped = snapDisplayValue(value, unit, joint.min_angle, joint.max_angle);
            dragJointValue(joint.name, unit.toNative(snapped));
          }}
          onChangeEnd={(value) => {
            const snapped = snapDisplayValue(value, unit, joint.min_angle, joint.max_angle);
            commitJointValue(joint.name, unit.toNative(snapped));
          }}
        />
        <ActionIcon
          variant="default"
          size={STEP_BUTTON_SIZE}
          aria-label={`Increase ${shortName}`}
          onClick={() => step(1)}
        >
          <IconPlus size={20} />
        </ActionIcon>
      </Group>

      {!compact && (
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {formatDisplayValue(sliderMin, unit)}
          </Text>
          <Text size="xs" c="dimmed">
            {formatDisplayValue(sliderMax, unit)}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
